import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { PageType } from "../../../../types/api";
import { useSiteState } from "../../../../utils/useSiteState";
import FormHolder from "../../../../components/Form/FormHolder";
import Form from "../../../../components/Form";

// // fake data generator
// const getItems = (count: number) =>
//   Array.from({ length: count }, (v, k) => k).map((k) => ({
//     id: `item-${k}`,
//     content: `item ${k}`,
//   }));

// a little function to help us with reordering the result
const reorder = (
  list: any,
  startIndex: number,
  endIndex: number
): PageType[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result as PageType[];
};

const grid = 8;

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  userSelect: "none",
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,
  borderRadius: 14,
  background: isDragging ? "#66B097" : "#110F15",
  ...draggableStyle,
});

const getListStyle = (isDraggingOver: boolean) => ({
  background: "#19181E",
  padding: grid,
  width: "100%",
  borderRadius: 14,
});

const sortPages = (a: PageType, b: PageType) => {
  if (a.sort < b.sort) return -1;
  if (a.sort > b.sort) return 1;
  return 0;
};

const SiteSettingsMenuPage = () => {
  const { site } = useSiteState();

  const getDefaults = () => {
    return {
      sort: [...site.pages].sort(sortPages) ?? [],
    };
  };

  const onSubmit = async (responseData: any) => {
    toast("Page ordering saved", { type: "success" });
  };

  return (
    <>
      <title>Edit Navigation</title>
      <FormHolder>
        <Form
          getDefaults={getDefaults}
          onCompleted={onSubmit}
          buttonTitle="Save Sort"
          endpoint={`sites/${site.id}/settings/menu`}
          method="put"
          transformRequestData={(data: { sort: PageType[] }) => {
            return {
              sort: data.sort.map((page, index) => ({ id: page.id, index })),
            };
          }}
          render={({ errors, control }) => (
            <>
              <h4 className="font-medium text-xl text-white">
                Manage Site Menu
              </h4>
              <p className="text-sm font-light text-gray-200">
                Manage your site menu
              </p>
              <div className="my-6">
                <Controller
                  name="sort"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({
                    field: { onChange, value },
                  }: any): JSX.Element => (
                    <DragDropContext
                      onDragEnd={async (result: any) => {
                        if (!result.destination) {
                          return;
                        }

                        const items: PageType[] = reorder(
                          value,
                          result.source.index,
                          result.destination.index
                        );

                        onChange(items);
                      }}
                    >
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={getListStyle(snapshot.isDraggingOver)}
                          >
                            {value.map((item: PageType, index: number) => (
                              <Draggable
                                key={item.id}
                                draggableId={item.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={getItemStyle(
                                      snapshot.isDragging,
                                      provided.draggableProps.style
                                    )}
                                  >
                                    {item.title}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                />
              </div>
            </>
          )}
        />
      </FormHolder>
    </>
  );
};

export default SiteSettingsMenuPage;
